import React, { createContext, useContext, useState, useCallback } from 'react';
import { fetchReservations, updateReservationStatus } from '../services/reservationService';
import { fetchReviews } from '../services/reviewService';

const DashboardContext = createContext();

/**
 * Ensures Booking ID strictly adheres to rule:
 * 5 characters long, lowercase letters (a-z) and numbers (0-9) only.
 */
export const formatBookingId = (rawId) => {
  if (!rawId) return 'a1b2c';
  let str = '';
  if (typeof rawId === 'object') {
    str = rawId.booking_id || rawId._id || rawId.id || '';
  } else {
    str = String(rawId);
  }
  const clean = str.replace(/[^a-z0-9]/gi, '').toLowerCase();
  if (clean.length >= 5) {
    return clean.slice(-5);
  }
  return clean.padStart(5, '0');
};

/**
 * Ensures Time Format strictly adheres to rule:
 * 24-hour format (HH:mm). No AM/PM allowed anywhere.
 */
export const format24HourTime = (timeInput) => {
  if (!timeInput) return '12:00';
  const str = String(timeInput).trim();
  
  // If already in 24h format HH:mm
  if (/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(str)) {
    const parts = str.split(':');
    return `${parts[0].padStart(2, '0')}:${parts[1]}`;
  }

  // Handle 12-hour format like 02:30 PM -> convert to 14:30
  const match12 = str.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (match12) {
    let hours = parseInt(match12[1], 10);
    const minutes = match12[2];
    const modifier = match12[3] ? match12[3].toUpperCase() : null;

    if (modifier === 'PM' && hours < 12) hours += 12;
    if (modifier === 'AM' && hours === 12) hours = 0;

    return `${String(hours).padStart(2, '0')}:${minutes}`;
  }

  // If Date object or ISO string
  const d = new Date(timeInput);
  if (!isNaN(d.getTime())) {
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  return '12:00';
};

export const DashboardProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    activeReservations: 0,
    totalReservations: 0
  });
  const [upcomingReservations, setUpcomingReservations] = useState([]);
  const [recentReservations, setRecentReservations] = useState([]);
  const [recentReviews, setRecentReviews] = useState([]);
  const [allReservations, setAllReservations] = useState([]);

  const getBusinessType = (category) => {
    const cat = (category || '').toLowerCase();
    if (cat === 'shop') return 'Shop';
    if (cat === 'restaurant') return 'Restaurant';
    if (cat === 'activity') return 'Task';
    return 'Shop';
  };

  const fetchDashboardData = useCallback(async (category, businessId, token) => {
    if (!businessId) return;
    setLoading(true);
    try {
      const businessType = getBusinessType(category);

      // 1. Fetch Reservations
      const resData = await fetchReservations(businessType, businessId, token);
      let list = [];
      if (Array.isArray(resData.data)) {
        list = resData.data;
      } else if (Array.isArray(resData.active) || Array.isArray(resData.inactive)) {
        list = [...(resData.active || []), ...(resData.inactive || [])];
      } else if (Array.isArray(resData.reservations)) {
        list = resData.reservations;
      } else if (Array.isArray(resData)) {
        list = resData;
      }

      setAllReservations(list);

      // Active = Pending or Confirmed
      const activeList = list.filter(b => b.status === 'Pending' || b.status === 'Confirmed');
      const totalCount = list.length;
      const activeCount = activeList.length;

      setStats({
        activeReservations: activeCount,
        totalReservations: totalCount
      });

      // Upcoming reservations (sorted by date/time ascending)
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      const upcoming = [...list]
        .filter(b => b.status !== 'Cancelled')
        .sort((a, b) => {
          const dA = a.date ? new Date(a.date) : new Date(a.createdAt);
          const dB = b.date ? new Date(b.date) : new Date(b.createdAt);
          return dA - dB;
        })
        .slice(0, 5);

      setUpcomingReservations(upcoming);

      // Recent reservations (sorted by createdAt / date descending)
      const recentRes = [...list]
        .sort((a, b) => {
          const dA = a.createdAt ? new Date(a.createdAt) : new Date(a.date);
          const dB = b.createdAt ? new Date(b.createdAt) : new Date(b.date);
          return dB - dA;
        })
        .slice(0, 5);

      setRecentReservations(recentRes);

      // 2. Fetch Reviews
      try {
        const revData = await fetchReviews(businessType, businessId, token);
        const reviewsArr = Array.isArray(revData) ? revData : (revData?.reviews || []);
        const recentRev = [...reviewsArr]
          .sort((a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date))
          .slice(0, 4);
        setRecentReviews(recentRev);
      } catch (revErr) {
        console.error('Error fetching reviews for dashboard:', revErr);
        setRecentReviews([]);
      }

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateStatus = async (reservationId, status, category, businessId, token) => {
    try {
      await updateReservationStatus(reservationId, status, token);
      await fetchDashboardData(category, businessId, token);
      return { success: true };
    } catch (err) {
      console.error('Failed to update reservation:', err);
      return { success: false, error: err.message };
    }
  };

  return (
    <DashboardContext.Provider
      value={{
        loading,
        stats,
        upcomingReservations,
        recentReservations,
        recentReviews,
        allReservations,
        fetchDashboardData,
        updateStatus
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboardContext = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboardContext must be used within a DashboardProvider');
  }
  return context;
};

export default DashboardContext;
